import { useController, useFormContext } from "react-hook-form";
import { format, formatISO, parseISO, startOfDay } from "date-fns";

import { Icon, Label, Text } from "@flare/ui/components";
import { cn } from "@flare/ui/utils";

import { Calendar } from "@/components/Funnel/base/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Funnel/base/Popover";

import { Hint } from "./base/Hint";

type DatePickerProps = {
  label: string;
  name: string;
  placeholder: string;
  required?: boolean;
  validationErrorMessage: string;
};

export function DatePicker({
  label,
  name,
  placeholder,
  required,
  validationErrorMessage,
}: DatePickerProps) {
  const {
    formState: { errors },
  } = useFormContext();
  const { field } = useController({
    name,
    rules: { required },
  });

  const handleSelectDate = (date: Date | undefined) => {
    date
      ? field.onChange(formatISO(date, { representation: "date" }))
      : field.onChange(undefined);
  };

  const currentDate = startOfDay(new Date());
  const hasError = Boolean(errors?.[name]);
  const selectedDate = field.value ? parseISO(field.value) : undefined;

  return (
    <Popover>
      <div className="flex flex-col gap-sp-2">
        <Label htmlFor={`${name}-btn`}>{label}</Label>
        <PopoverTrigger asChild>
          <button
            id={`${name}-btn`}
            className={cn(
              "flex h-s-6 w-full appearance-none items-center justify-start gap-sp-2 rounded-sm border bg-transparent pl-sp-4 pr-sp-3 text-body1 text-(--color-input-content) outline-none transition placeholder:text-(--color-input-placeholder)",
              "border-(--color-input-stroke) hover:border-(--color-input-stroke-hover) focus:border-(--color-input-stroke-active)",
              "data-[state=open]:border-(--color-input-stroke-active)",
              {
                "border-(--color-input-stroke-danger) hover:border-(--color-input-stroke-danger) focus:border-(--color-input-stroke-danger) data-[state=open]:border-(--color-input-stroke-danger)":
                  hasError,
              },
            )}
          >
            <Icon
              name="calendar"
              className={cn({
                "text-(--color-input-placeholder)": !field.value,
              })}
            />
            {field.value ? (
              <Text>{format(selectedDate!, "PPP")}</Text>
            ) : (
              <Text textColor="disclaimer">{placeholder}</Text>
            )}
          </button>
        </PopoverTrigger>
        {hasError && (
          <Hint id={`${name}-error`} error={hasError}>
            {validationErrorMessage}
          </Hint>
        )}
      </div>
      <PopoverContent className="w-auto border border-primary p-sp-0">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelectDate}
          disabled={{ before: currentDate }}
          defaultMonth={selectedDate || currentDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
